import Vue from 'vue';
import { PAGINATION, REPORTS_API } from '../utils/constants.js';
import { storeGet } from '@/store/store-utils';

/**
 * Reports Store Module
 * Handles all report-related data and API calls
 */
export default {
    namespaced: true,

    state: {
        dataReportData: [],
        dataReportListLoading: false,
        dataReportFilterLoading: false,
        dataReportTotalPages: 0,
        dataReportHeader: [
            { text: 'S.No', value: 'indexno',width: '70px' },
            { text: 'Cycle Period', value: 'period_detail' },
            { text: 'Requested on', value: 'created_at' },
            { text: 'Status', value: 'status', filterable: true, filterList: ['generating', 'ready', 'failed'] },
            { text: 'Actions', value: 'action', width: '100px' },
        ],
        cycleReportData: [],
        cycleReportListLoading: false,
        cycleReportFilterLoading: false,
        cycleReportTotalPages: 0,
        cycleReportHeader: [
            { text: 'S.No', value: 'indexno',width: '70px' },
            { text: 'Reporting Period', value: 'period_detail' },
            { text: 'Requested on', value: 'created_at' },
            { text: 'Status', value: 'status', filterable: true, filterList: ['generating', 'ready', 'failed'] },
            { text: 'Actions', value: 'action',width: '100px' }
        ],
        userAccessReportData: [],
        userAccessReportListLoading: false,
        userAccessReportFilterLoading: false,
        userAccessReportTotalPages: 0,
        userAccessReportHeader: [
            { text: 'S.No', value: 'indexno', width: '70px' },
            { text: 'Access Scope', value: 'role_display' },
            { text: 'Requested on', value: 'created_at' },
            { text: 'Status', value: 'status', filterable: true, filterList: ['generating', 'ready', 'failed'] },
            { text: 'Actions', value: 'action', width: '100px' },
        ],
        materialityReportData: [],
        materialityReportListLoading: false,
        materialityReportFilterLoading: false,
        materialityReportTotalPages: 0,
        materialityReportHeader: [
            { text: 'S.No', value: 'indexno', width: '70px' },
            { text: 'Materiality Context', value: 'materiality_type' },
            { text: 'Requested on', value: 'created_at' },
            { text: 'Status', value: 'status', filterable: true, filterList: ['generating', 'ready', 'failed'] },
            { text: 'Actions', value: 'action', width: '100px' },
        ],
        approvalsReportData: [],
        approvalsReportListLoading: false,
        approvalsReportFilterLoading: false,
        approvalsReportTotalPages: 0,
        approvalsReportHeader: [
            { text: 'S.No', value: 'indexno', width: '70px' },
            { text: 'Request Type', value: 'filter' },
            { text: 'Requested on', value: 'created_at' },
            { text: 'Status', value: 'status', filterable: true, filterList: ['generating', 'ready', 'failed'] },
            { text: 'Actions', value: 'action', width: '100px' },
        ],
        dataCentreReportData: [],
        dataCentreReportListLoading: false,
        dataCentreReportFilterLoading: false,
        dataCentreReportTotalPages: 0,
        dataCentreReportHeader: [
            { text: 'S.No', value: 'indexno', width: '70px' },
            { text: 'Data Context', value: 'data_context' },
            { text: 'Requested On', value: 'created_at' },
            { text: 'Status', value: 'status', filterable: true, filterList: ['generating', 'ready', 'failed'] },
            { text: 'Actions', value: 'action', width: '100px' },
        ],
        
    },

    mutations: {
        /**
         * Sets the data report data in state
         * @param {Object} state - Vuex state
         * @param {Array} data - Data report data
         */
        setDataReportData(state, data) {
            state.dataReportData = data;
        },

        /**
         * Sets the loading state for data report list
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setDataReportListLoading(state, loading) {
            state.dataReportListLoading = loading;
        },

        /**
         * Sets the loading state for data report filter
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setDataReportFilterLoading(state, loading) {
            state.dataReportFilterLoading = loading;
        },

        /**
         * Sets the total pages for data report
         * @param {Object} state - Vuex state
         * @param {Number} totalPages - Total pages count
         */
        setDataReportTotalPages(state, totalPages) {
            state.dataReportTotalPages = totalPages;
        },

        /**
         * Sets the cycle report data in state
         * @param {Object} state - Vuex state
         * @param {Array} data - Cycle report data
         */
        setCycleReportData(state, data) {
            state.cycleReportData = data;
        },

        /**
         * Sets the loading state for cycle report list
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setCycleReportListLoading(state, loading) {
            state.cycleReportListLoading = loading;
        },

        /**
         * Sets the loading state for cycle report filter
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setCycleReportFilterLoading(state, loading) {
            state.cycleReportFilterLoading = loading;
        },

        /**
         * Sets the total pages for cycle report
         * @param {Object} state - Vuex state
         * @param {Number} totalPages - Total pages count
         */
        setCycleReportTotalPages(state, totalPages) {
            state.cycleReportTotalPages = totalPages;
        },

        /**
         * Sets the user access report data in state
         * @param {Object} state - Vuex state
         * @param {Array} data - User access report data
         */
        setUserAccessReportData(state, data) {
            state.userAccessReportData = data;
        },

        /**
         * Sets the loading state for user access report list
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setUserAccessReportListLoading(state, loading) {
            state.userAccessReportListLoading = loading;
        },

        /**
         * Sets the loading state for user access report filter
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setUserAccessReportFilterLoading(state, loading) {
            state.userAccessReportFilterLoading = loading;
        },

        /**
         * Sets the total pages for user access report
         * @param {Object} state - Vuex state
         * @param {Number} totalPages - Total pages count
         */
        setUserAccessReportTotalPages(state, totalPages) {
            state.userAccessReportTotalPages = totalPages;
        },

        /**
         * Sets the materiality report data in state
         * @param {Object} state - Vuex state
         * @param {Array} data - Materiality report data
         */
        setMaterialityReportData(state, data) {
            state.materialityReportData = data;
        },

        /**
         * Sets the loading state for materiality report list
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setMaterialityReportListLoading(state, loading) {
            state.materialityReportListLoading = loading;
        },

        /**
         * Sets the loading state for materiality report filter
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setMaterialityReportFilterLoading(state, loading) {
            state.materialityReportFilterLoading = loading;
        },

        /**
         * Sets the total pages for materiality report
         * @param {Object} state - Vuex state
         * @param {Number} totalPages - Total pages count
         */
        setMaterialityReportTotalPages(state, totalPages) {
            state.materialityReportTotalPages = totalPages;
        },

        /**
         * Sets the approvals report data in state
         * @param {Object} state - Vuex state
         * @param {Array} data - Approvals report data
         */
        setApprovalsReportData(state, data) {
            state.approvalsReportData = data;
        },

        /**
         * Sets the loading state for approvals report list
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setApprovalsReportListLoading(state, loading) {
            state.approvalsReportListLoading = loading;
        },

        /**
         * Sets the loading state for approvals report filter
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setApprovalsReportFilterLoading(state, loading) {
            state.approvalsReportFilterLoading = loading;
        },

        /**
         * Sets the total pages for approvals report
         * @param {Object} state - Vuex state
         * @param {Number} totalPages - Total pages count
         */
        setApprovalsReportTotalPages(state, totalPages) {
            state.approvalsReportTotalPages = totalPages;
        },

        /**
         * Sets the data centre report data in state
         * @param {Object} state - Vuex state
         * @param {Array} data - Data centre report data
         */
        setDataCentreReportData(state, data) {
            state.dataCentreReportData = data;
        },

        /**
         * Sets the loading state for data centre report list
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setDataCentreReportListLoading(state, loading) {
            state.dataCentreReportListLoading = loading;
        },

        /**
         * Sets the loading state for data centre report filter
         * @param {Object} state - Vuex state
         * @param {Boolean} loading - Loading state
         */
        setDataCentreReportFilterLoading(state, loading) {
            state.dataCentreReportFilterLoading = loading;
        },

        /**
         * Sets the total pages for data centre report
         * @param {Object} state - Vuex state
         * @param {Number} totalPages - Total pages count
         */
        setDataCentreReportTotalPages(state, totalPages) {
            state.dataCentreReportTotalPages = totalPages;
        },
    },

    actions: {
        /**
         * Fetches data report list for initial load (paginated)
         * API: /v3/varianceanalysis/analysis/downloadallruexcellist/
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Pagination payload { page, limit }
         */
        async fetchDataReportList({ commit, rootState }, payload = {}) {
            try {
                commit('setDataReportListLoading', true);
                
                const params = {
                    page: payload.page || PAGINATION.DEFAULT_PAGE,
                    limit: payload.limit || PAGINATION.DEFAULT_LIMIT,
                    cycletype: payload.cycletype || 'Actual',
                    type: payload.type || 'allruexcel_' + (payload.cycletype || 'Actual').toLowerCase(),
                    reporting_type: payload.reporting_type,
                    ...(payload.status ? { status: payload.status } : {})
                };
                
                const response = await storeGet(
                    Vue.axios, rootState,
                    REPORTS_API.DOWNLOAD_ALL_RU_EXCEL_LIST,
                    params
                );
                
                if (response.data?.status === 'success') {
                    commit('setDataReportData', response.data.data || []);
                    commit('setDataReportTotalPages', response.data.totalPages || 0);
                } else {
                    commit('setDataReportData', []);
                    commit('setDataReportTotalPages', 0);
                    commit('sbError', response.data?.message || 'Failed to fetch data report list', { root: true });
                }
                return response;
            } catch (error) {
                commit('setDataReportData', []);
                commit('setDataReportTotalPages', 1);
                commit('sbError', error.response?.data?.message || 'Failed to fetch data report list', { root: true });
                return { data: { status: 'error', message: error.message } };
            } finally {
                commit('setDataReportListLoading', false);
            }
        },

        /**
         * Fetches data report data with filters applied
         * API: /v3/varianceanalysis/analysis/downloadallruexcel/
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Filter payload { network, country, region, ruType, reportingunit, type }
         */
        async fetchDataReportFiltered({ commit }, payload = {}) {
            try {
                commit('setDataReportFilterLoading', true);
                
                const requestPayload = {
                    filter: {
                        period: payload.period || 'all',
                        network: payload.network || 'all',
                        country: payload.country || 'all',
                        region: payload.region || 'all',
                        ruType: payload.ruType || 'all',
                        reportingunit: payload.reportingunit || 'all',
                        type: payload.type || 'allruexcel_actual',
                        module: payload.module || 'all',
                        cycletype: payload.cycletype || 'Actual',
                    }
                };
                
                let url = REPORTS_API.DOWNLOAD_ALL_RU_EXCEL;
                if (payload.id) {
                    url += payload.id;
                }

                const response = await Vue.axios.post(
                    url,
                    requestPayload
                );
                
                if (response.data?.status === 'success') {
                    commit('setDataReportData', response.data.data || []);
                    commit('sbSuccess', 'Data report generated successfully', { root: true });
                } else {
                    commit('setDataReportData', []);
                    commit('sbError', response.data?.message || 'Failed to fetch filtered data report', { root: true });
                }
                return response;
            } catch (error) {
                commit('setDataReportData', []);
                commit('sbError', error.response?.data?.message || 'Failed to fetch filtered data report', { root: true });
                return { data: { status: 'error', message: error.message } };
            } finally {
                commit('setDataReportFilterLoading', false);
            }
        },

        /**
         * Fetches cycle report list for initial load (paginated)
         * API: /v3/varianceanalysis/analysis/downloadallruexcellist/
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Pagination payload { page, limit }
         */
        async fetchCycleReportList({ commit, rootState }, payload = {}) {
            try {
                commit('setCycleReportListLoading', true);
                
                const params = {
                    page: payload.page || PAGINATION.DEFAULT_PAGE,
                    limit: payload.limit || PAGINATION.DEFAULT_LIMIT,
                    cycletype: payload.cycletype || 'Actual',
                    type: payload.type || 'allrucycle_' + (payload.cycletype || 'Actual').toLowerCase(),
                    reporting_type: payload.reporting_type,
                    ...(payload.status ? { status: payload.status } : {})
                };
                
                const response = await storeGet(
                    Vue.axios, rootState,
                    REPORTS_API.DOWNLOAD_ALL_RU_EXCEL_LIST,
                    params
                );
                
                if (response.data?.status === 'success') {
                    commit('setCycleReportData', response.data.data || []);
                    commit('setCycleReportTotalPages', response.data.totalPages || 0);
                } else {
                    commit('setCycleReportData', []);
                    commit('setCycleReportTotalPages', 0);
                    commit('sbError', response.data?.message || 'Failed to fetch cycle report list', { root: true });
                }
                return response;
            } catch (error) {
                commit('setCycleReportData', []);
                commit('setCycleReportTotalPages', 0);
                commit('sbError', error.response?.data?.message || 'Failed to fetch cycle report list', { root: true });
                return { data: { status: 'error', message: error.message } };
            } finally {
                commit('setCycleReportListLoading', false);
            }
        },

        /**
         * Fetches cycle report data with filters applied
         * API: /v3/varianceanalysis/analysis/downloadallruexcel/
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Filter payload { network, country, region, ruType, reportingunit }
         */
        async fetchCycleReportFiltered({ commit }, payload = {}) {
            try {
                commit('setCycleReportFilterLoading', true);
                
                const requestPayload = {
                    filter: {
                        period: payload.period || 'all',
                        network: payload.network || 'all',
                        country: payload.country || 'all',
                        region: payload.region || 'all',
                        ruType: payload.ruType || 'all',
                        reportingunit: payload.reportingunit || 'all',
                        type: payload.type || 'allrucycle_actual',
                        reporting_type: payload.reporting_type,
                        cycletype:payload.cycletype || 'Actual',
                    }
                };
                
                let url = REPORTS_API.DOWNLOAD_ALL_RU_EXCEL;
                if (payload.id) {
                    url += payload.id;
                }

                const response = await Vue.axios.post(
                    url,
                    requestPayload
                );
                
                if (response.data?.status === 'success') {
                    commit('setCycleReportData', response.data.data || []);
                    commit('sbSuccess', 'Cycle report generated successfully', { root: true });
                } else {
                    commit('setCycleReportData', []);
                    commit('sbError', response.data?.message || 'Failed to fetch filtered cycle report', { root: true });
                }
                return response;
            } catch (error) {
                commit('setCycleReportData', []);
                commit('sbError', error.response?.data?.message || 'Failed to fetch filtered cycle report', { root: true });
                return { data: { status: 'error', message: error.message } };
            } finally {
                commit('setCycleReportFilterLoading', false);
            }
        },

        /**
         * Fetches user access list for initial load (paginated)
         * API: /v3/varianceanalysis/reportingunit/downloadallaccessexcellist
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Pagination payload { page, limit }
         */
        async fetchUserAccessList({ commit, rootState }, payload = {}) {
            try {
                commit('setUserAccessReportListLoading', true);
                
                const params = {
                    page: payload.page || PAGINATION.DEFAULT_PAGE,
                    limit: payload.limit || PAGINATION.DEFAULT_LIMIT,
                    cycletype: payload.cycletype || 'Actual',
                    type: payload.type,
                    ...(payload.status ? { status: payload.status } : {})
                };
                
                const response = await storeGet(
                    Vue.axios, rootState,
                    REPORTS_API.DOWNLOAD_ALL_ACCESS_EXCEL_LIST,
                    params
                );
                
                if (response.data?.status === 'success') {
                    commit('setUserAccessReportData', response.data.data || []);
                    commit('setUserAccessReportTotalPages', response.data.totalPages || 0);
                } else {
                    commit('setUserAccessReportData', []);
                    commit('setUserAccessReportTotalPages', 0);
                    commit('sbError', response.data?.message || 'Failed to fetch user access list', { root: true });
                }
            } catch (error) {
                commit('setUserAccessReportData', []);
                commit('setUserAccessReportTotalPages', 0);
                commit('sbError', error.response?.data?.message || 'Failed to fetch user access list', { root: true });
            } finally {
                commit('setUserAccessReportListLoading', false);
            }
        },

        /**
         * Fetches user access data with filters applied
         * API: /v2/varianceanalysis/reportingunit/downloadallaccessexcel/
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Filter payload
         */
        async fetchUserAccess({ commit }, payload = {}) {
            try {
                commit('setUserAccessReportFilterLoading', true);
                
                const requestPayload = {
                    filter: {
                        role: payload.role || 'all',
                        network: payload.network || 'all',
                        country: payload.country || 'all',
                        region: payload.region || 'all',
                        reportingunit: payload.reportingunit || 'all',
                        type: payload.type,
                        cycletype: payload.cycletype || 'Actual',
                        reporting_type: payload.reporting_type,
                        module: payload.module
                    }
                };
                
                const response = await Vue.axios.post(
                    REPORTS_API.DOWNLOAD_ALL_ACCESS_EXCEL,
                    requestPayload
                );
                
                if (response.data?.status === 'success') {
                    commit('setUserAccessReportData', response.data.data || []);
                    commit('sbSuccess', 'User access report generated successfully', { root: true });
                } else {
                    commit('setUserAccessReportData', []);
                    commit('sbError', response.data?.message || 'Failed to fetch user access report', { root: true });
                }
            } catch (error) {
                commit('setUserAccessReportData', []);
                commit('sbError', error.response?.data?.message || 'Failed to fetch user access report', { root: true });
            } finally {
                commit('setUserAccessReportFilterLoading', false);
            }
        },

        /**
         * Fetches materiality report list for initial load (paginated)
         * API: /v3/varianceanalysis/reportingunit/downloadallRUMaterialityexcellist
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Pagination payload { page, limit }
         */
        async fetchMaterialityReportList({ commit, rootState }, payload = {}) {
            try {
                commit('setMaterialityReportListLoading', true);
                
                const params = {
                    page: payload.page || PAGINATION.DEFAULT_PAGE,
                    limit: payload.limit || PAGINATION.DEFAULT_LIMIT,
                    cycletype: payload.cycletype || 'Actual',
                    type: payload.type || 'allrumateriality_' + (payload.cycletype || 'Actual').toLowerCase(),
                    ...(payload.status ? { status: payload.status } : {})
                };
                
                const response = await storeGet(
                    Vue.axios, rootState,
                    REPORTS_API.DOWNLOAD_ALL_MATERIALITY_EXCEL_LIST,
                    params
                );
                
                if (response.data?.status === 'success') {
                    commit('setMaterialityReportData', response.data.data || []);
                    commit('setMaterialityReportTotalPages', response.data.totalPages || 0);
                } else {
                    commit('setMaterialityReportData', []);
                    commit('setMaterialityReportTotalPages', 0);
                    commit('sbError', response.data?.message || 'Failed to fetch materiality report list', { root: true });
                }
            } catch (error) {
                commit('setMaterialityReportData', []);
                commit('setMaterialityReportTotalPages', 0);
                commit('sbError', error.response?.data?.message || 'Failed to fetch materiality report list', { root: true });
            } finally {
                commit('setMaterialityReportListLoading', false);
            }
        },

        /**
         * Fetches materiality report data with filters applied
         * API: /v3/varianceanalysis/reportingunit/downloadallRUMaterialityexcel
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Filter payload
         */
        async fetchMaterialityReportFiltered({ commit }, payload = {}) {
            try {
                commit('setMaterialityReportFilterLoading', true);
                
                const requestPayload = {
                    filter: {
                        period: payload.period || 'all',
                        network: payload.network || 'all',
                        country: payload.country || 'all',
                        region: payload.region || 'all',
                        reportingunit: payload.reportingunit || 'all',
                        type: payload.type || 'allrumateriality_actual',
                        cycletype: payload.cycletype || 'actual',
                        materiality_type: payload.materiality_type || 'ru',
                    }
                };
                
                const response = await Vue.axios.post(
                    REPORTS_API.DOWNLOAD_ALL_MATERIALITY_EXCEL,
                    requestPayload
                );
                
                if (response.data?.status === 'success') {
                    commit('setMaterialityReportData', response.data.data || []);
                    commit('sbSuccess', 'Materiality report generated successfully', { root: true });
                } else {
                    commit('setMaterialityReportData', []);
                    commit('sbError', response.data?.message || 'Failed to fetch filtered materiality report', { root: true });
                }
            } catch (error) {
                commit('setMaterialityReportData', []);
                commit('sbError', error.response?.data?.message || 'Failed to fetch filtered materiality report', { root: true });
            } finally {
                commit('setMaterialityReportFilterLoading', false);
            }
        },

        /**
         * Fetches approvals report list for initial load (paginated)
         * API: /v3/varianceanalysis/reportingunit/downloadallapprovalsllist
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Pagination payload { page, limit }
         */
        async fetchApprovalsReportList({ commit, rootState }, payload = {}) {
            try {
                commit('setApprovalsReportListLoading', true);
                
                const params = {
                    page: payload.page || PAGINATION.DEFAULT_PAGE,
                    limit: payload.limit || PAGINATION.DEFAULT_LIMIT,
                    type: 'approval_report',
                    ...(payload.status ? { status: payload.status } : {})
                };
                
                const response = await storeGet(
                    Vue.axios, rootState,
                    REPORTS_API.DOWNLOAD_ALL_APPROVALS_LIST,
                    params
                );
                
                if (response.data?.status === 'success') {
                    commit('setApprovalsReportData', response.data.data || []);
                    commit('setApprovalsReportTotalPages', response.data.totalPages || 0);
                } else {
                    commit('setApprovalsReportData', []);
                    commit('setApprovalsReportTotalPages', 0);
                    commit('sbError', response.data?.message || 'Failed to fetch approvals report list', { root: true });
                }
            } catch (error) {
                commit('setApprovalsReportData', []);
                commit('setApprovalsReportTotalPages', 0);
                commit('sbError', error.response?.data?.message || 'Failed to fetch approvals report list', { root: true });
            } finally {
                commit('setApprovalsReportListLoading', false);
            }
        },

        /**
         * Fetches approvals report data with filters applied
         * API: /v3/varianceanalysis/analysis/downloadapprovalreport
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Filter payload
         */
        async fetchApprovalsReportFiltered({ commit }, payload = {}) {
            try {
                commit('setApprovalsReportFilterLoading', true);
                
                const requestPayload = {
                    data:{
                        type:"approval_report",
                        module: payload.module || 'all',
                    },
                    filter: {
                        request_type: payload.request_type || 'all',
                        status: payload.status || 'all',
                    }
                };
                
                const response = await Vue.axios.post(
                    REPORTS_API.DOWNLOAD_APPROVAL_REPORT,
                    requestPayload
                );
                
                if (response.data?.status === 'success') {
                    commit('setApprovalsReportData', response.data.data || []);
                    commit('sbSuccess', 'Approvals report generated successfully', { root: true });
                } else {
                    commit('setApprovalsReportData', []);
                    commit('sbError', response.data?.message || 'Failed to fetch filtered approvals report', { root: true });
                }
            } catch (error) {
                commit('setApprovalsReportData', []);
                commit('sbError', error.response?.data?.message || 'Failed to fetch filtered approvals report', { root: true });
            } finally {
                commit('setApprovalsReportFilterLoading', false);
            }
        },

        /**
         * Fetches data centre report list for initial load (paginated)
         * API: /v3/varianceanalysis/analysis/datacentrereportlist
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Pagination payload { page, limit }
         */
        async fetchDataCentreReportList({ commit, rootState }, payload = {}) {
            try {
                commit('setDataCentreReportListLoading', true);
                
                const params = {
                    page: payload.page || PAGINATION.DEFAULT_PAGE,
                    limit: payload.limit || PAGINATION.DEFAULT_LIMIT,
                    type: "data_centre_report",
                    ...(payload.status ? { status: payload.status } : {}),
                };
                
                const response = await storeGet(
                    Vue.axios, rootState,
                    REPORTS_API.DATA_CENTRE_REPORT_LIST,
                    params
                );

                
                
                if (response.data?.status === 'success') {
                    commit('setDataCentreReportData', response.data.data || []);
                    commit('setDataCentreReportTotalPages', response.data.totalPages || 0);
                } else {
                    commit('setDataCentreReportData', []);
                    commit('setDataCentreReportTotalPages', 0);
                    commit('sbError', response.data?.message || 'Failed to fetch data centre report list', { root: true });
                }
            } catch (error) {
                commit('setDataCentreReportData', []);
                commit('setDataCentreReportTotalPages', 0);
                commit('sbError', error.response?.data?.message || 'Failed to fetch data centre report list', { root: true });
            } finally {
                commit('setDataCentreReportListLoading', false);
            }
        },

        /**
         * Fetches data centre report data with filters applied
         * API: /v3/varianceanalysis/analysis/datacentrereport
         * @param {Object} context - Vuex action context
         * @param {Object} payload - Filter payload
         */
        async fetchDataCentreReportFiltered({ commit, rootState }, payload = {}) {
            try {
                commit('setDataCentreReportFilterLoading', true);
                
                const requestPayload = {
                    data: {
                        type: "data_centre_report",
                        module: payload.module || 'all',
                    },
                    filter: {
                        request_type: payload.batch_type || 'all',
                        status: payload.status || 'all',
                        start_date: payload.start_date || '',
                        end_date: payload.end_date || '',
                    },
                    client: rootState.client,
                };
                
                const response = await Vue.axios.post(
                    REPORTS_API.DOWNLOAD_DATA_CENTRE_REPORT,
                    requestPayload
                );
                
                if (response.data?.status === 'success') {
                    commit('setDataCentreReportData', response.data.data || []);
                    commit('sbSuccess', 'Data centre report generated successfully', { root: true });
                } else {
                    commit('setDataCentreReportData', []);
                    commit('sbError', response.data?.message || 'Failed to fetch filtered data centre report', { root: true });
                }
            } catch (error) {
                commit('setDataCentreReportData', []);
                commit('sbError', error.response?.data?.message || 'Failed to fetch filtered data centre report', { root: true });
            } finally {
                commit('setDataCentreReportFilterLoading', false);
            }
        },
    },

    getters: {
        /**
         * Gets the data report data
         * @param {Object} state - Vuex state
         * @returns {Array} Data report data
         */
        getDataReportData: (state) => state.dataReportData,

        /**
         * Gets the data report list loading state
         * @param {Object} state - Vuex state
         * @returns {Boolean} Loading state
         */
        getDataReportListLoading: (state) => state.dataReportListLoading,

        /**
         * Gets the data report filter loading state
         * @param {Object} state - Vuex state
         * @returns {Boolean} Loading state
         */
        getDataReportFilterLoading: (state) => state.dataReportFilterLoading,
        getDataReportHeader: (state) => state.dataReportHeader,
        getDataReportTotalPages: (state) => state.dataReportTotalPages,

        /**
         * Gets the cycle report data
         * @param {Object} state - Vuex state
         * @returns {Array} Cycle report data
         */
        getCycleReportData: (state) => state.cycleReportData,

        /**
         * Gets the cycle report list loading state
         * @param {Object} state - Vuex state
         * @returns {Boolean} Loading state
         */
        getCycleReportListLoading: (state) => state.cycleReportListLoading,

        /**
         * Gets the cycle report filter loading state
         * @param {Object} state - Vuex state
         * @returns {Boolean} Loading state
         */
        getCycleReportFilterLoading: (state) => state.cycleReportFilterLoading,
        getCycleReportHeader: (state) => state.cycleReportHeader,
        getCycleReportTotalPages: (state) => state.cycleReportTotalPages,
        getUserAccessReportData: (state) => state.userAccessReportData,
        getUserAccessReportListLoading: (state) => state.userAccessReportListLoading,
        getUserAccessReportFilterLoading: (state) => state.userAccessReportFilterLoading,
        getUserAccessReportHeader: (state) => state.userAccessReportHeader,
        getUserAccessReportTotalPages: (state) => state.userAccessReportTotalPages,
        getMaterialityReportData: (state) => state.materialityReportData,
        getMaterialityReportListLoading: (state) => state.materialityReportListLoading,
        getMaterialityReportFilterLoading: (state) => state.materialityReportFilterLoading,
        getMaterialityReportHeader: (state) => state.materialityReportHeader,
        getMaterialityReportTotalPages: (state) => state.materialityReportTotalPages,
        getApprovalsReportData: (state) => state.approvalsReportData,
        getApprovalsReportListLoading: (state) => state.approvalsReportListLoading,
        getApprovalsReportFilterLoading: (state) => state.approvalsReportFilterLoading,
        getApprovalsReportHeader: (state) => state.approvalsReportHeader,
        getApprovalsReportTotalPages: (state) => state.approvalsReportTotalPages,
        getDataCentreReportData: (state) => state.dataCentreReportData,
        getDataCentreReportListLoading: (state) => state.dataCentreReportListLoading,
        getDataCentreReportFilterLoading: (state) => state.dataCentreReportFilterLoading,
        getDataCentreReportHeader: (state) => state.dataCentreReportHeader,
        getDataCentreReportTotalPages: (state) => state.dataCentreReportTotalPages,
    },
};
